import { useCallback, useEffect, useRef, useState } from "react";

/* ═══════════════════════════════════════════════════════════════════════════
   MotionDetector  v2  — clean, confident detections only
   Pipeline:
     1. Downsample video → 160×90 processing canvas  (~15 fps)
     2. Per-block (4×4) luma diff vs previous frame
     3. Temporal accumulator — a block must move in ≥2 consecutive frames
        before it is flagged (kills single-frame noise)
     4. Morphological dilation (radius 2) — fills intra-object gaps so the
        edges of a moving person merge into one region instead of fragments
     5. Flood-fill labelling — connected blobs only
     6. Screen-space box merging — nearby/overlapping rects become one
     7. Min-size & max-count guards — prevent tiny blobs and clutter
     8. Lerp tracking across frames — smooth following
   ═══════════════════════════════════════════════════════════════════════════ */

// ── processing constants ────────────────────────────────────────────────────
const PROC_W      = 160;
const PROC_H      = 90;
const BLOCK       = 4;
const GRID_W      = PROC_W / BLOCK;                // 40
const GRID_H      = Math.floor(PROC_H / BLOCK);    // 22

const LUMA_THR    = 34;    // raised: less noise triggers
const ACCUM_MAX   = 5;     // max accumulator value per block
const ACCUM_ON    = 2;     // accumulator threshold to confirm motion
const DILATION    = 2;     // grid-block dilation radius
const MIN_BLOCKS  = 14;    // raised: minimum connected blocks per region
const MARGIN      = 12;    // px breathing room around each detected region
const MERGE_PAD   = 48;    // px: merge screen-space boxes closer than this
const MIN_DIM     = 60;    // px: ignore boxes smaller than this on either axis
const MAX_BOXES   = 4;     // cap: never show more than this many boxes
const LERP        = 0.22;  // position smoothing (lower = smoother)
const DEAD_LIMIT  = 8;     // frames before evicting a lost box
const MATCH_D2    = 140 * 140; // px² centre distance to match tracked box

// ── colours by intensity ────────────────────────────────────────────────────
const RGB_T = "66,246,215";   // teal   — calm tracking
const RGB_O = "255,153,64";   // orange — active motion
const RGB_R = "255,107,107";  // red    — high intensity

function pickRgb(intensity) {
  if (intensity > 0.06) return RGB_R;
  if (intensity > 0.025) return RGB_O;
  return RGB_T;
}

/* ── morphological dilation — fills gaps inside moving objects ────────────── */
function dilate(grid, radius) {
  const out = new Uint8Array(GRID_W * GRID_H);
  for (let gy = 0; gy < GRID_H; gy++) {
    for (let gx = 0; gx < GRID_W; gx++) {
      if (!grid[gy * GRID_W + gx]) continue;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const ny = gy + dy, nx = gx + dx;
          if (ny >= 0 && ny < GRID_H && nx >= 0 && nx < GRID_W)
            out[ny * GRID_W + nx] = 1;
        }
      }
    }
  }
  return out;
}

/* ── flood-fill connected-component labelling ────────────────────────────── */
function findRegions(grid) {
  const vis = new Uint8Array(GRID_W * GRID_H);
  const out = [];
  for (let gy = 0; gy < GRID_H; gy++) {
    for (let gx = 0; gx < GRID_W; gx++) {
      const i = gy * GRID_W + gx;
      if (!grid[i] || vis[i]) continue;
      const stk = [i]; vis[i] = 1;
      let x0 = gx, x1 = gx, y0 = gy, y1 = gy, n = 0;
      while (stk.length) {
        const cur = stk.pop();
        const cy  = (cur / GRID_W) | 0, cx = cur % GRID_W;
        n++;
        if (cx < x0) x0 = cx; if (cx > x1) x1 = cx;
        if (cy < y0) y0 = cy; if (cy > y1) y1 = cy;
        if (cx + 1 < GRID_W && grid[cur + 1]      && !vis[cur + 1])      { vis[cur + 1] = 1;      stk.push(cur + 1); }
        if (cx - 1 >= 0     && grid[cur - 1]      && !vis[cur - 1])      { vis[cur - 1] = 1;      stk.push(cur - 1); }
        if (cy + 1 < GRID_H && grid[cur + GRID_W] && !vis[cur + GRID_W]) { vis[cur + GRID_W] = 1; stk.push(cur + GRID_W); }
        if (cy - 1 >= 0     && grid[cur - GRID_W] && !vis[cur - GRID_W]) { vis[cur - GRID_W] = 1; stk.push(cur - GRID_W); }
      }
      if (n >= MIN_BLOCKS) out.push({ x0, x1, y0, y1, n });
    }
  }
  return out;
}

/* ── object-fit: cover — map processing region → display pixel coords ────── */
function regionToScreen(r, video) {
  const cw = video.clientWidth, ch = video.clientHeight;
  const nw = video.videoWidth  || 1, nh = video.videoHeight || 1;
  const sc = Math.max(cw / nw, ch / nh);
  const ox = (cw - nw * sc) / 2, oy = (ch - nh * sc) / 2;

  const nx0 = (r.x0 * BLOCK / PROC_W) * nw, ny0 = (r.y0 * BLOCK / PROC_H) * nh;
  const nx1 = ((r.x1 + 1) * BLOCK / PROC_W) * nw, ny1 = ((r.y1 + 1) * BLOCK / PROC_H) * nh;

  // Raw box with breathing-room margin
  const rawX  = ox + nx0 * sc - MARGIN;
  const rawY  = oy + ny0 * sc - MARGIN;
  const rawX2 = ox + nx1 * sc + MARGIN;
  const rawY2 = oy + ny1 * sc + MARGIN;

  // Clamp to viewport — prevents brackets bleeding off-screen edges
  const x = Math.max(2, rawX);
  const y = Math.max(2, rawY);

  return {
    x, y,
    w: Math.min(cw - 2, rawX2) - x,
    h: Math.min(ch - 2, rawY2) - y,
  };
}

/* ── merge screen-space boxes that overlap or are close to each other ─────── */
function mergeBoxes(boxes) {
  if (boxes.length <= 1) return boxes;
  const list = boxes.map((b) => ({ ...b }));
  let again = true;
  while (again) {
    again = false;
    outer: for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i], b = list[j];
        // expand both by MERGE_PAD then check overlap
        if (a.x - MERGE_PAD < b.x + b.w && a.x + a.w + MERGE_PAD > b.x &&
            a.y - MERGE_PAD < b.y + b.h && a.y + a.h + MERGE_PAD > b.y) {
          const nx = Math.min(a.x, b.x), ny = Math.min(a.y, b.y);
          list[i] = {
            x: nx, y: ny,
            w: Math.max(a.x + a.w, b.x + b.w) - nx,
            h: Math.max(a.y + a.h, b.y + b.h) - ny,
            intensity: Math.max(a.intensity, b.intensity),
          };
          list.splice(j, 1);
          again = true; break outer;
        }
      }
    }
  }
  return list;
}

/* ── canvas drawing ─────────────────────────────────────────────────────── */
const CORNER_FRAC = 0.22;   // bracket length as fraction of shortest edge

function safeRoundRect(ctx, x, y, w, h, r) {
  if (typeof ctx.roundRect === "function") ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
}

function drawBox(ctx, x, y, w, h, rgb, alpha, pulse) {
  const cl  = Math.min(w * CORNER_FRAC, h * CORNER_FRAC, 32);
  const lw  = 1.6 + pulse * 1.0;

  ctx.save();

  /* ghost fill — very subtle tint */
  ctx.globalAlpha = alpha * 0.07;
  ctx.fillStyle   = `rgba(${rgb},.9)`;
  ctx.fillRect(x, y, w, h);

  /* ghost border */
  ctx.globalAlpha = alpha * 0.10;
  ctx.strokeStyle = `rgba(${rgb},1)`;
  ctx.lineWidth   = 0.6;
  ctx.shadowBlur  = 0;
  ctx.strokeRect(x, y, w, h);

  /* centre crosshair — very faint, gives depth */
  const mx = x + w / 2, my = y + h / 2;
  ctx.globalAlpha = alpha * 0.14;
  ctx.strokeStyle = `rgba(${rgb},1)`;
  ctx.lineWidth   = 0.7;
  ctx.beginPath();
  ctx.arc(mx, my, 2.5, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(mx - 5, my); ctx.lineTo(mx + 5, my);
  ctx.moveTo(mx, my - 5); ctx.lineTo(mx, my + 5);
  ctx.stroke();

  /* corner brackets — the hero element */
  ctx.globalAlpha = (0.80 + pulse * 0.20) * alpha;
  ctx.strokeStyle = `rgba(${rgb},1)`;
  ctx.lineWidth   = lw;
  ctx.lineCap     = "square";
  ctx.shadowColor = `rgba(${rgb},1)`;
  ctx.shadowBlur  = 4 + pulse * 9;

  const corners = [
    [x + cl, y,     x,     y,     x,         y + cl   ],
    [x+w-cl, y,     x + w, y,     x + w,     y + cl   ],
    [x,      y+h-cl,x,     y + h, x + cl,    y + h    ],
    [x+w,    y+h-cl,x + w, y + h, x + w - cl,y + h    ],
  ];
  for (const [ax, ay, bx, by, cx2, cy2] of corners) {
    ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(cx2, cy2); ctx.stroke();
  }

  ctx.restore();
}

/* Label pill anchored INSIDE the box — top-left corner, clear of the bracket arm */
function drawLabel(ctx, x, y, w, label, rgb, alpha) {
  if (alpha < 0.18 || w < 80) return;
  ctx.save();
  ctx.globalAlpha  = alpha;
  ctx.font         = "700 8px 'SF Pro Text',system-ui,sans-serif";
  ctx.textBaseline = "middle";
  const tw = ctx.measureText(label).width;
  const pw = 5, bh = 14;
  /* 9 px from left & top edges — sits just inside the corner bracket arms */
  const lx = x + 9, ly = y + 9;

  ctx.fillStyle = "rgba(0,0,0,.72)";
  ctx.beginPath(); safeRoundRect(ctx, lx, ly, tw + pw * 2, bh, 2.5); ctx.fill();

  ctx.strokeStyle = `rgba(${rgb},.85)`;
  ctx.lineWidth   = 0.7;
  ctx.shadowColor = `rgba(${rgb},.65)`;
  ctx.shadowBlur  = 5;
  ctx.stroke();

  ctx.fillStyle  = `rgba(${rgb},1)`;
  ctx.shadowBlur = 0;
  ctx.fillText(label, lx + pw, ly + bh / 2);
  ctx.restore();
}

/* Small numbered circle in the top-right of each box — only shown with 2+ detections */
function drawIndex(ctx, x, y, w, idx, rgb, alpha) {
  if (alpha < 0.18 || idx <= 0) return;
  const r  = 8;
  const cx = x + w - 9 - r;
  const cy = y + 9 + r;
  ctx.save();
  ctx.globalAlpha = alpha * 0.90;

  ctx.fillStyle  = "rgba(0,0,0,.68)";
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();

  ctx.strokeStyle = `rgba(${rgb},.80)`;
  ctx.lineWidth   = 0.8;
  ctx.shadowColor = `rgba(${rgb},.55)`;
  ctx.shadowBlur  = 5;
  ctx.stroke();

  ctx.fillStyle    = `rgba(${rgb},1)`;
  ctx.font         = "800 8px system-ui,sans-serif";
  ctx.textAlign    = "center";
  ctx.textBaseline = "middle";
  ctx.shadowBlur   = 0;
  ctx.fillText(String(idx), cx, cy);
  ctx.restore();
}

/* ═══════════════════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════════════════ */
export default function MotionDetector({ videoRef, active }) {
  const cvRef    = useRef(null);
  const prevPx   = useRef(null);
  const prevDims = useRef({ w: 0, h: 0 });
  const accum    = useRef(null);    // temporal accumulator grid
  const boxes    = useRef([]);
  const nextId   = useRef(0);
  const rafId    = useRef(null);
  const frameN   = useRef(0);
  const proc     = useRef(null);

  const [count, setCount] = useState(0);

  /* persistent off-screen processing canvas */
  if (!proc.current)
    proc.current = Object.assign(document.createElement("canvas"), { width: PROC_W, height: PROC_H });

  const stopLoop = useCallback(() => {
    cancelAnimationFrame(rafId.current); rafId.current = null;
    prevPx.current = null; prevDims.current = { w: 0, h: 0 };
    if (accum.current) accum.current.fill(0);
    boxes.current = []; frameN.current = 0;
    const cv = cvRef.current;
    if (cv) cv.getContext("2d")?.clearRect(0, 0, cv.width, cv.height);
    setCount(0);
  }, []);

  useEffect(() => {
    if (!active) { stopLoop(); return; }

    const pCtx = proc.current.getContext("2d", { willReadFrequently: true });

    const tick = () => {
      rafId.current = requestAnimationFrame(tick);
      frameN.current++;

      const video = videoRef.current, cv = cvRef.current;
      if (!video || !cv || video.readyState < 2) return;
      if (frameN.current % 2 !== 0) return;   // ~15 fps

      /* sync overlay canvas size to video element */
      const cw = video.clientWidth, ch = video.clientHeight;
      if (!cw || !ch) return;
      if (cv.width !== cw || cv.height !== ch) { cv.width = cw; cv.height = ch; }

      /* reset on camera source change (e.g. front/back flip) */
      if (prevDims.current.w !== video.videoWidth || prevDims.current.h !== video.videoHeight) {
        prevDims.current = { w: video.videoWidth, h: video.videoHeight };
        prevPx.current   = null;
        if (accum.current) accum.current.fill(0);
      }

      /* capture downsampled frame */
      pCtx.drawImage(video, 0, 0, PROC_W, PROC_H);
      const curr = pCtx.getImageData(0, 0, PROC_W, PROC_H).data;

      const ctx = cv.getContext("2d");
      ctx.clearRect(0, 0, cw, ch);

      if (!prevPx.current || prevPx.current.length !== curr.length) {
        prevPx.current = new Uint8Array(curr); return;
      }
      const prev = prevPx.current;

      /* initialise accumulator if needed */
      if (!accum.current || accum.current.length !== GRID_W * GRID_H)
        accum.current = new Uint8Array(GRID_W * GRID_H);
      const ac = accum.current;

      /* ── Step 1: per-block luma diff → temporal accumulator ───────────── */
      for (let gy = 0; gy < GRID_H; gy++) {
        for (let gx = 0; gx < GRID_W; gx++) {
          let sum = 0;
          for (let py = 0; py < BLOCK; py++) {
            for (let px = 0; px < BLOCK; px++) {
              const pi = ((gy * BLOCK + py) * PROC_W + (gx * BLOCK + px)) * 4;
              sum += Math.abs(curr[pi]   - prev[pi])   * 0.299
                   + Math.abs(curr[pi+1] - prev[pi+1]) * 0.587
                   + Math.abs(curr[pi+2] - prev[pi+2]) * 0.114;
            }
          }
          const avg = sum / (BLOCK * BLOCK);
          const i   = gy * GRID_W + gx;
          // ramp up fast, decay slow → hysteresis keeps confirmed motion stable
          ac[i] = avg > LUMA_THR
            ? Math.min(ACCUM_MAX, ac[i] + 2)
            : ac[i] > 0 ? ac[i] - 1 : 0;
        }
      }
      prevPx.current = new Uint8Array(curr);

      /* ── Step 2: threshold accumulator → raw binary grid ─────────────── */
      const rawGrid = new Uint8Array(GRID_W * GRID_H);
      for (let i = 0; i < rawGrid.length; i++)
        rawGrid[i] = ac[i] >= ACCUM_ON ? 1 : 0;

      /* ── Step 3: morphological dilation — fills gaps inside objects ───── */
      const grid = dilate(rawGrid, DILATION);

      /* ── Step 4: flood-fill → regions → screen boxes ─────────────────── */
      const regions = findRegions(grid);

      const raw = regions.map((r) => {
        const scr = regionToScreen(r, video);
        return { ...scr, intensity: r.n / (GRID_W * GRID_H) };
      });

      /* ── Step 5: filter small boxes, merge nearby, cap count ─────────── */
      const filtered = raw.filter((b) => b.w >= MIN_DIM && b.h >= MIN_DIM);
      const merged   = mergeBoxes(filtered);
      // Sort by intensity descending and keep top N
      const newBoxes = merged
        .sort((a, b) => b.intensity - a.intensity)
        .slice(0, MAX_BOXES);

      /* ── Step 6: lerp-track across frames ────────────────────────────── */
      const used = new Set();
      for (const tb of boxes.current) {
        const tcx = tb.x + tb.w / 2, tcy = tb.y + tb.h / 2;
        let bestD = MATCH_D2, bestI = -1;
        newBoxes.forEach((b, i) => {
          if (used.has(i)) return;
          const d = (b.x + b.w / 2 - tcx) ** 2 + (b.y + b.h / 2 - tcy) ** 2;
          if (d < bestD) { bestD = d; bestI = i; }
        });
        if (bestI >= 0) {
          const b = newBoxes[bestI];
          tb.x += (b.x - tb.x) * LERP; tb.y += (b.y - tb.y) * LERP;
          tb.w += (b.w - tb.w) * LERP; tb.h += (b.h - tb.h) * LERP;
          tb.intensity = b.intensity;
          tb.dead = 0; tb.age = Math.min(tb.age + 1, 30);
          used.add(bestI);
        } else tb.dead++;
      }
      newBoxes.forEach((b, i) => {
        if (!used.has(i)) boxes.current.push({ id: nextId.current++, ...b, dead: 0, age: 0 });
      });
      boxes.current = boxes.current.filter((b) => b.dead < DEAD_LIMIT);

      const live = boxes.current.filter((b) => b.dead === 0).length;
      setCount(live);

      /* ── Step 7: render ──────────────────────────────────────────────── */
      const t     = performance.now() / 1000;
      const pulse = (Math.sin(t * 3.8) + 1) / 2;

      // Assign stable visual indices to confirmed live boxes (sorted left→right)
      const confirmed = boxes.current
        .filter((b) => b.dead === 0 && b.age >= 2)
        .sort((a, b2) => a.x - b2.x);
      const idxMap = new Map(confirmed.map((b, i) => [b.id, i + 1]));
      const multiDetect = confirmed.length > 1;

      for (const b of boxes.current) {
        // Only show after ≥2 confirmed frames; fade in/out
        if (b.age < 2 && b.dead === 0) continue;
        const alpha = Math.min(1, (b.age - 1) / 4) * (1 - b.dead / DEAD_LIMIT);
        if (alpha < 0.05) continue;

        const rgb    = pickRgb(b.intensity);
        const pScale = b.intensity > 0.06 ? 1.0 : b.intensity > 0.025 ? 0.6 : 0.35;

        drawBox(ctx, b.x, b.y, b.w, b.h, rgb, alpha, pulse * pScale);

        // Label pill — inside box top-left, correct signature (x, y, w, label, rgb, alpha)
        const tag = b.intensity > 0.06 ? "● ACTIVE" : b.intensity > 0.025 ? "● MOTION" : "TRACKING";
        drawLabel(ctx, b.x, b.y, b.w, tag, rgb, alpha);

        // Numbered circle top-right — only when 2+ detections are live
        if (multiDetect) {
          drawIndex(ctx, b.x, b.y, b.w, idxMap.get(b.id) ?? 0, rgb, alpha);
        }
      }
    };

    rafId.current = requestAnimationFrame(tick);
    return stopLoop;
  }, [active, videoRef, stopLoop]);

  return (
    <>
      {/* Transparent detection overlay */}
      <canvas ref={cvRef} className="mdet-canvas" aria-hidden="true" />

      {/* HUD pill */}
      {active && (
        <div className={`mdet-hud${count > 0 ? " has-hits" : ""}`} aria-live="polite" aria-atomic="true">
          <span className="mdet-radar" aria-hidden="true">
            <span className="mdet-radar-ring" />
            <span className="mdet-radar-core" />
          </span>
          <span className="mdet-hud-label">MOTION DETECT</span>
          {count > 0 && <span className="mdet-hud-count">{count} DETECTED</span>}
        </div>
      )}
    </>
  );
}
